import { observer } from 'mobx-react-lite';
import * as React from 'react';
import loc from 'owa-localize';
import { isEdu } from 'owa-nonboot-userconfiguration-manager';
import {
    reconnectAccount,
    removeAccount,
    trueTimeToggle,
    trueTimeToolTip,
    trueTimeToolTipForSchool,
} from './ConnectedAccount.locstring.json';
import { OWAConnectedAccount, OWAConnectedAccountState } from 'owa-accounts-store';
import {
    lazyGetStoreIsSettled,
    lazyAddMergedViewAvailabilityAccount,
    lazyRemoveMergedViewAvailabilityAccount,
} from 'owa-truetime';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { TooltipHost, DirectionalHint } from 'office-ui-fabric-react/lib/Tooltip';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Toggle } from 'office-ui-fabric-react/lib/Toggle';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { Spinner, SpinnerSize, ISpinnerStyles } from 'office-ui-fabric-react/lib/Spinner';

import {
    getAccountProviderTypeIcon,
    getAccountProviderTypeLabel,
    isTrueTimeEnabledForProvider,
    getAccountAddressForMergedView,
} from 'owa-accounts-utils';
import getAccountPendingTrueTimeUpdate from '../selectors/getAccountPendingTrueTimeUpdate';
import setTrueTimeUpdateFailedAccountMutator from '../mutators/setTrueTimeUpdateFailedAccountMutator';
import setAccountPendingTrueTimeUpdateMutator from '../mutators/setAccountPendingTrueTimeUpdateMutator';
import { DefaultPalette } from '@uifabric/styling/lib/index';
import { logUsage } from 'owa-analytics';
import { startRemovingAccount, startAddingAccount } from '../actions/internalActions';
import { AccountsEditorState } from '../store/schema/AccountsEditorState';
import setAccountsEditorState from '../mutators/setAccountsEditorState';
import getErrorMessage from '../selectors/getErrorMessage';
import getAccountsEditorSource from '../selectors/getAccountsEditorSource';

const styles = require('./ConnectedAccount.scss');
const accountProviderStyles = require('./AccountProvider.scss');
const classNames = require('classnames/bind').bind(accountProviderStyles);

export interface ConnectedAccountProps {
    account: OWAConnectedAccount;
    mergeViewEnabledAccount: string;
}

export default observer(function ConnectedAccount(props: ConnectedAccountProps) {
    const accountsEditorSource = getAccountsEditorSource();

    React.useEffect(() => {
        if (shouldDisableTrueTimeToggle()) {
            logUsage('TrueTimeServerClientMismatch');
        }
    }, []);
    const shouldDisableTrueTimeToggle = () => {
        return (
            props.mergeViewEnabledAccount &&
            props.mergeViewEnabledAccount != getAccountAddressForMergedView(props.account)
        );
    };
    const removeButtonOnClick = async () => {
        const { account, mergeViewEnabledAccount } = props;
        startRemovingAccount(account, mergeViewEnabledAccount);
    };
    const reconnectButtonOnClick = async () => {
        const { account /*, mergeViewEnabledAccount*/ } = props;
        // remove account and then show the auth dialog for add - until VSO 58686 is fixed
        // startRemovingAccount(account, mergeViewEnabledAccount, true /*reconnect flow*/);
        startAddingAccount(account.accountProviderType, true /*reconnect flow*/);
    };
    const enableTrueTimeToggleOnChange = async (event, checked) => {
        const { account } = props;
        // Clear the error message while we wait for a response
        setTrueTimeUpdateFailedAccountMutator(null);
        setAccountsEditorState(AccountsEditorState.None);
        // Show the spinner while waiting for a response from the server
        setAccountPendingTrueTimeUpdateMutator(account.userIdentity);
        if (checked == true) {
            const addMergedViewAvailabilityAccount = await lazyAddMergedViewAvailabilityAccount.import();
            await addMergedViewAvailabilityAccount(
                account.userIdentity,
                account.accountUniqueId,
                account.accountProviderType,
                accountsEditorSource
            );
        } else {
            const removeMergedViewAvailabilityAccount = await lazyRemoveMergedViewAvailabilityAccount.import();
            await removeMergedViewAvailabilityAccount(
                account.accountProviderType,
                accountsEditorSource
            );
        }
        // Hide the spinner now that we have a response from the server
        setAccountPendingTrueTimeUpdateMutator(null);
    };
    const { account, mergeViewEnabledAccount } = props;
    const getStoreIsSettled = lazyGetStoreIsSettled.tryImportForRender();
    const errorMessage = getErrorMessage(account);
    const isTrueTimeStoreSettled = getStoreIsSettled ? getStoreIsSettled() : false;
    const isUpdatePending = getAccountPendingTrueTimeUpdate() == account.userIdentity;
    const showTrueTimeSpinner = !isTrueTimeStoreSettled || isUpdatePending;
    const showTruetimeToggle = isTrueTimeEnabledForProvider(account.accountProviderType);
    const accountProviderTypeIconClass = account.accountProviderType + 'Icon';
    return (
        <div className={styles.accountContainer}>
            <div className={styles.accountDetailsContainer}>
                <img
                    className={classNames(accountProviderTypeIconClass)}
                    src={getAccountProviderTypeIcon(account.accountProviderType)}
                />
                <div className={styles.accountDescriptionColumn}>
                    <p className={styles.accountName}>{account.userIdentity}</p>
                    <p className={styles.accountProvider}>
                        {getAccountProviderTypeLabel(account.accountProviderType)}
                    </p>
                </div>
                <DefaultButton
                    disabled={isUpdatePending}
                    className={styles.removeAccountButton}
                    text={loc(removeAccount)}
                    onClick={removeButtonOnClick}
                />
                {account.accountState != OWAConnectedAccountState.Valid && (
                    <DefaultButton
                        disabled={isUpdatePending}
                        className={styles.removeAccountButton}
                        text={loc(reconnectAccount)}
                        onClick={reconnectButtonOnClick}
                    />
                )}
            </div>
            {showTruetimeToggle && (
                <div className={styles.trueTimeContainer}>
                    {showTrueTimeSpinner ? (
                        <Spinner
                            styles={getSpinnerStyle()}
                            size={SpinnerSize.large}
                            className={styles.trueTimeToggleSpinner}
                            label={loc(trueTimeToggle)}
                            labelPosition="right"
                        />
                    ) : (
                        <div className={styles.trueTimeToggleContainer}>
                            <Toggle
                                ariaLabel={
                                    isEdu()
                                        ? loc(trueTimeToggle) + loc(trueTimeToolTipForSchool)
                                        : loc(trueTimeToggle) + loc(trueTimeToolTip)
                                }
                                disabled={shouldDisableTrueTimeToggle()}
                                className={styles.trueTimeToggle}
                                checked={
                                    mergeViewEnabledAccount ==
                                    getAccountAddressForMergedView(props.account)
                                }
                                onChange={enableTrueTimeToggleOnChange}
                            />
                            <Label id="trueTimeLabelId" className={styles.trueTimeLabel}>
                                {loc(trueTimeToggle)}
                            </Label>
                            <TooltipHost
                                hostClassName={styles.trueTimeToolTipHost}
                                content={
                                    isEdu() ? loc(trueTimeToolTipForSchool) : loc(trueTimeToolTip)
                                }
                                directionalHint={DirectionalHint.rightCenter}>
                                <Icon
                                    iconName={ControlIcons.Info}
                                    className={styles.trueTimeInfoIcon}
                                />
                            </TooltipHost>
                        </div>
                    )}
                </div>
            )}
            {errorMessage != null && (
                <div className={styles.errorContainer}>
                    <Icon className={styles.errorIcon} iconName={ControlIcons.Error} />
                    <p className={styles.errorMessage}>{errorMessage} </p>
                </div>
            )}
        </div>
    );
});

function getSpinnerStyle(): ISpinnerStyles {
    return {
        circle: {
            margin: '0 0 0 22px',
        },
        label: {
            margin: '0 0 0 22px',
            color: DefaultPalette.neutralPrimary,
            fontSize: '14px',
            fontWeight: '600',
        },
    };
}
